import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container, Page, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import { FooterSeparator } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { gridTableAnalysisActions } from '@constants/actionTypes';
import { useDispatch } from 'react-redux';
import { TrackingEvent } from '@classes/TrackingEvent';
import { DEFAULT_CREDIT_MODE_PROMPTS, DEFAULT_DOC_TEMPLATE_AGENT_MODE_PROMPTS, GRID_MODES } from './index';
import { FONT } from '@constants/app';
import { useColorTheme } from '@hooks';
import { useTableAnalysis } from '@hooks/features/useTableAnalysis';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { includesId, logNetRequest } from '@utils/usefulFuncs';
import { DocumentSelectorCell } from '@containers/TableAnalysis/tableComponents/DocumentSelectorCell';
import { PromptRow } from '@containers/TableAnalysis/tableComponents/PromptRow';
import { AddPromptRow } from '@containers/TableAnalysis/tableComponents/AddPromptRow';
import ErrorText from '@ui/ErrorText';
import { FlatButton } from '@ui/buttons';
import { ManageFavoriteQuestionSharing } from '@containers/TableAnalysis/modes/ManageFavoriteQuestionSharing';
import { Header } from './Header';
import { AI_TOOLS, DOC_SELECT_MODES } from '@constants';
import { useCurrentUserToolsAvailable, useIsTornadoEmployee } from '@hooks/user/useCurrentUser';
import { StandaloneDocSelectModal } from '@containers/docSelection/StandaloneDocSelectModal';
import { AgentSelect } from '@containers/DigitalAnalyst/components/AgentSelect';

const Wrapper = styled.div``;
const DocSelectActionsWrapper = styled.div`
  padding-top: 24px;

  display: flex;
  flex-direction: row;
  gap: 16px;
`;
const AgentSelectWrapper = styled.div`
  max-width: 320px;
`;
const TableWrapper = styled.div`
  overflow: scroll;

  padding-top: 24px;
  min-height: 412px;

  table {
    width: 100%;

    th,
    td {
      color: ${({ theme }) => theme.themeColors.text};
      border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
      border-radius: 1px;
    }

    th {
      font-size: 12px;
      line-height: 14px;
      font-family: ${() => FONT.bold};
      min-width: 120px;
      padding: 0;
    }

    td {
      padding: 12px 8px 12px 20px;
      font-size: 12px;
      line-height: 14px;
      min-width: 300px;
      font-family: ${() => FONT.normal};
    }
  }
`;

function _isDisabledFromDocs(selectedDocuments) {
  return selectedDocuments.length === 1 && selectedDocuments[0] === null;
}

const selectDefaultDocSelectMode = (docMode) => {
  if (docMode === GRID_MODES.fed) {
    return DOC_SELECT_MODES.speaker;
  }
  if (docMode === GRID_MODES.liquidityTerms) {
    return DOC_SELECT_MODES.ppm;
  }
  if (docMode === GRID_MODES.credit) {
    return DOC_SELECT_MODES.credit;
  }
  if (docMode === GRID_MODES.misc) {
    return DOC_SELECT_MODES.allCompany;
  }
  if (docMode === GRID_MODES.ppm) {
    return DOC_SELECT_MODES.ppm;
  }
  if (docMode === GRID_MODES.client) {
    return DOC_SELECT_MODES.client;
  }

  return DOC_SELECT_MODES.allCompany;
};

const selectAvailableDocSelectModes = (docMode, { isCompanyTaggingEnabled }) => {
  const companyTagFallback = isCompanyTaggingEnabled ? [DOC_SELECT_MODES.companyTag] : [];
  if (docMode === GRID_MODES.fed) {
    return [DOC_SELECT_MODES.speaker, DOC_SELECT_MODES.docType];
  }
  if (docMode === GRID_MODES.credit) {
    return [DOC_SELECT_MODES.credit, ...companyTagFallback];
  }
  if (docMode === GRID_MODES.liquidityTerms) {
    return [DOC_SELECT_MODES.ppm, ...companyTagFallback];
  }
  if (docMode === GRID_MODES.misc) {
    return [DOC_SELECT_MODES.allCompany, DOC_SELECT_MODES.docType];
  }
  if (docMode === GRID_MODES.ppm) {
    return [DOC_SELECT_MODES.ppm, ...companyTagFallback];
  }
  if (docMode === GRID_MODES.client) {
    return [DOC_SELECT_MODES.client];
  }
  return [DOC_SELECT_MODES.allCompany, DOC_SELECT_MODES.docType];
};

const ColumnDocSelectionModal = ({
  index,
  docMode,
  docs: selectedDocs,

  handleAddDocument,
  handleRemoveDocument,

  onDismiss,
}) => {
  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCompanyTaggingEnabled = toolsAvailable.includes(AI_TOOLS.COMPANY_TAGGING);

  const [docSelectModes] = React.useState(selectAvailableDocSelectModes(docMode, { isCompanyTaggingEnabled }));

  const bindIndexToAddDocument = (docOrDocs) => handleAddDocument(index)(docOrDocs);
  const bindIndexToRemoveDocument = (docOrDocs) => handleRemoveDocument(index)(docOrDocs);

  return (
    <StandaloneDocSelectModal
      docSelectModes={docSelectModes}
      defaultActiveMode={selectDefaultDocSelectMode(docMode)}
      heading={`Select Document(s) for Column ${index + 1}`}
      docMode={docMode}
      selectedDocs={selectedDocs}
      handleAddDocument={bindIndexToAddDocument}
      handleRemoveDocument={bindIndexToRemoveDocument}
      onDismiss={onDismiss}
    />
  );
};

const MultiColumnDocSelectionModal = ({ docMode, handleAddDocuments, onDismiss }) => {
  const [selectedDocs, setSelectedDocs] = React.useState([]);

  const toolsAvailable = useCurrentUserToolsAvailable();
  const isCompanyTaggingEnabled = toolsAvailable.includes(AI_TOOLS.COMPANY_TAGGING);

  const [docSelectModes] = React.useState(selectAvailableDocSelectModes(docMode, { isCompanyTaggingEnabled }));

  const handleAddDocument = (docOrDocs) => {
    setSelectedDocs((prevState) =>
      Array.isArray(docOrDocs) ? [...prevState, ...docOrDocs] : [...prevState, docOrDocs]
    );
  };
  const handleRemoveDocument = (docOrDocs) => {
    if (Array.isArray(docOrDocs)) {
      setSelectedDocs((prevState) => prevState.filter((d) => !includesId(docOrDocs, d.id)));
    } else {
      setSelectedDocs((prevState) => prevState.filter((d) => d.id !== docOrDocs.id));
    }
  };

  const overrideOnDismiss = () => {
    handleAddDocuments(selectedDocs);
    onDismiss();
  };

  return (
    <StandaloneDocSelectModal
      docSelectModes={docSelectModes}
      defaultActiveMode={selectDefaultDocSelectMode(docMode)}
      heading={`Select multiple documents, each will be placed in a separate column`}
      docMode={docMode}
      selectedDocs={selectedDocs}
      handleAddDocument={handleAddDocument}
      handleRemoveDocument={handleRemoveDocument}
      onDismiss={overrideOnDismiss}
    />
  );
};

const SectorMultiColumnDocSelectionModal = ({
  docMode,
  handleAddDocuments,
  handleAddPerColumnDocuments,
  onDismiss,
}) => {
  const [docSelectModes] = React.useState([DOC_SELECT_MODES.sector]);

  const handleAddDocument = (docOrDocs) => {
    handleAddPerColumnDocuments(docOrDocs);
    onDismiss();
  };

  const overrideOnDismiss = () => {
    onDismiss();
  };

  return (
    <StandaloneDocSelectModal
      docSelectModes={docSelectModes}
      defaultActiveMode={docSelectModes[0]}
      heading={`Add documents to each column`}
      docMode={docMode}
      handleAddDocument={handleAddDocument}
      onDismiss={overrideOnDismiss}
    />
  );
};

const BuildTableQuestionDocumentSelector = ({ onRunAnalysis, docMode, selectedAgent, onSelectedAgentChange }) => {
  const dispatch = useDispatch();
  const colorTheme = useColorTheme();
  const isTornadoEmployee = useIsTornadoEmployee();

  const [docSelectionColumnIndex, setDocSelectionColumnIndex] = React.useState(null);
  const showSelector = docSelectionColumnIndex !== null;
  const [showMultiColumnSelector, setShowMultiColumnSelector] = React.useState(false);
  const [showSectorMultiColumnSelector, setShowSectorMultiColumnSelector] = React.useState(false);

  const [initializing, setInitializing] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [initError, setInitError] = React.useState(null);

  const tableState = useTableAnalysis();
  const { prompts, selectedDocuments } = tableState;

  const numberOfColumns = selectedDocuments.length;
  const disableRunAnalysis = _isDisabledFromDocs(selectedDocuments);
  const showAgentSelect = docMode === GRID_MODES.docTemplateAgent && isTornadoEmployee;

  const dismissDocSelection = () => setDocSelectionColumnIndex(null);

  // init
  React.useEffect(() => {
    const getFavorites = async (userMode) => {
      const URL = 'v1/genai_favorite_prompts?user_mode=' + userMode;
      const { status, data } = await sendApiRequest('get', URL);
      logNetRequest(status, data);
      handle400Statuses(status);
      if (status === 200) {
        const prompts = data.prompts;
        const defaults = prompts.filter((p) => p.is_default);
        return {
          defaultPrompts: defaults,
          favQs: prompts.filter((p) => !p.is_default),
        };
      } else {
        setInitError(true);
        return {};
      }
    };

    const initFedMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: defaultPrompts,
      });
      setInitializing(false);
    };
    const initPPMMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: defaultPrompts,
      });
      setInitializing(false);
    };
    const initCreditMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: [...DEFAULT_CREDIT_MODE_PROMPTS, ...defaultPrompts],
      });
      setInitializing(false);
    };

    const initLiquidityTermsMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);

      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: defaultPrompts,
      });
      setInitializing(false);
    };
    const initGenericMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: defaultPrompts,
      });
      setInitializing(false);
    };
    const initDocTemplateAgentMode = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: DEFAULT_DOC_TEMPLATE_AGENT_MODE_PROMPTS,
      });
      setInitializing(false);
    };
    const initCustomClientModes = async () => {
      const { favQs, defaultPrompts } = await getFavorites(docMode);
      dispatch({
        type: gridTableAnalysisActions.replaceFavoriteQuestions,
        payload: [...favQs, ...defaultPrompts],
      });
      dispatch({
        type: gridTableAnalysisActions.replacePrompts,
        payload: defaultPrompts,
      });
      setInitializing(false);
    };
    if (docMode === GRID_MODES.fed) initFedMode();
    if (docMode === GRID_MODES.credit) initCreditMode();
    if (docMode === GRID_MODES.ppm) initPPMMode();
    if (docMode === GRID_MODES.liquidityTerms) initLiquidityTermsMode();
    if (docMode === GRID_MODES.misc) initGenericMode();
    if (docMode === GRID_MODES.docTemplateAgent) initDocTemplateAgentMode();
    if (docMode === GRID_MODES.clientPeerAnalysis) initCustomClientModes();
    if (docMode === GRID_MODES.client) initCustomClientModes();
  }, []);

  const handleAddDocuments = (documents) => {
    dispatch({
      type: gridTableAnalysisActions.addSelectedDocuments,
      payload: documents,
    });
  };

  const handleAddPerColumnDocuments = (documents) => {
    dispatch({
      type: gridTableAnalysisActions.addSelectedPerColumnDocuments,
      payload: documents,
    });
  };

  const handleDocSelect = (i) => (documentOrDocuments) => {
    dispatch({
      type: gridTableAnalysisActions.addSelectedDocument,
      payload: { index: i, documentOrDocuments },
    });

    if (Array.isArray(documentOrDocuments)) {
      documentOrDocuments.forEach((d) => {
        TrackingEvent.create('Add Document To Table', {
          Id: d?.id,
          Filename: d?.filename,
        });
      });
    } else {
      TrackingEvent.create('Add Document To Table', {
        Id: documentOrDocuments?.id,
        Filename: documentOrDocuments?.filename,
      });
    }

    setError(null);
  };

  const handleDocRemove = (i) => (documentOrDocuments) => {
    dispatch({
      type: gridTableAnalysisActions.removeSelectedDocument,
      payload: { index: i, documentOrDocuments },
    });

    setError(null);
  };

  const handleDocColumnClear = (i) => () => {
    dispatch({
      type: gridTableAnalysisActions.removeSelectedDocument,
      payload: { index: i, clearAll: true },
    });

    setError(null);
  };

  const handleRunAnalysisClick = () => {
    if (prompts.length === 0) {
      setError('Please enter or select questions.');
      return;
    }
    if (disableRunAnalysis) {
      setError('Please select documents to analyze.');
      return;
    }
    setError(null);

    TrackingEvent.create('Run Analysis', {});

    onRunAnalysis({
      prompts,
      docs: selectedDocuments.filter((d) => d !== null),
      selectedAgent,
    });
  };

  const handleSelectDocumentsPerColumnClick = () => {
    setShowMultiColumnSelector(true);
  };
  const handleSelectCompsDocumentsClick = () => {
    setShowSectorMultiColumnSelector(1);
  };

  const onOpenDocSelect = (i) => setDocSelectionColumnIndex(i);

  if (initError) {
    return (
      <Container top={80} bottom={100}>
        <Container centerAll>
          <Body1>Something went wrong, please refresh and try again.</Body1>
        </Container>
      </Container>
    );
  }

  if (initializing) {
    return (
      <Container top={80} bottom={100}>
        <Container centerAll>
          <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
          <Container left={16}>
            <Body1>Loading...</Body1>
          </Container>
        </Container>
      </Container>
    );
  }

  return (
    <Wrapper>
      <DocSelectActionsWrapper>
        {/* {docMode === GRID_MODES.misc && (
          <SkeletonButton onClick={handleSelectCompsDocumentsClick}>Find Companies To Compare</SkeletonButton>
        )} */}
        <SkeletonButton onClick={handleSelectDocumentsPerColumnClick}>Select Documents (1 per column)</SkeletonButton>
      </DocSelectActionsWrapper>
      {showAgentSelect && (
        <Container top={24}>
          <Container bottom={8}>
            <Body5 bold>Agent</Body5>
          </Container>
          <AgentSelectWrapper>
            <AgentSelect value={selectedAgent} onChange={onSelectedAgentChange} />
          </AgentSelectWrapper>
        </Container>
      )}
      {showMultiColumnSelector && (
        <MultiColumnDocSelectionModal
          docMode={docMode}
          handleAddDocuments={handleAddDocuments}
          onDismiss={() => setShowMultiColumnSelector(false)}
        />
      )}
      {showSectorMultiColumnSelector && (
        <SectorMultiColumnDocSelectionModal
          docMode={docMode}
          handleAddPerColumnDocuments={handleAddPerColumnDocuments}
          onDismiss={() => setShowSectorMultiColumnSelector(false)}
        />
      )}
      {showSelector && (
        <ColumnDocSelectionModal
          index={docSelectionColumnIndex}
          docMode={docMode}
          docs={selectedDocuments[docSelectionColumnIndex]}
          handleAddDocument={handleDocSelect}
          handleRemoveDocument={handleDocRemove}
          onDismiss={dismissDocSelection}
        />
      )}
      <TableWrapper>
        <table>
          <tbody>
            <tr>
              <td>
                <Body5 bold>Question</Body5>
              </td>
              {selectedDocuments.map((s, i) => (
                <DocumentSelectorCell
                  key={i}
                  index={i}
                  docs={s}
                  onOpenDocSelect={onOpenDocSelect}
                  removeDocs={handleDocColumnClear(i)}
                />
              ))}
            </tr>
            {prompts.map((p, i) => (
              <PromptRow key={i} index={i} prompt={p} numberOfColumns={numberOfColumns} docMode={docMode} />
            ))}
            <tr>
              <td colSpan={selectedDocuments.length + 1}>
                <AddPromptRow />
              </td>
            </tr>
          </tbody>
        </table>
      </TableWrapper>

      <Container top={24}>
        {error && (
          <Container bottom={16}>
            <ErrorText>{error}</ErrorText>
          </Container>
        )}
        <FlatButton fullWidth onClick={handleRunAnalysisClick} disabled={disableRunAnalysis}>
          Run Analysis
        </FlatButton>
      </Container>
    </Wrapper>
  );
};

const TableFooterOptions = ({ docMode }) => {
  const dispatch = useDispatch();
  const [mode, setMode] = React.useState(null);

  const handleClearImportedTableClick = () => {
    dispatch({
      type: gridTableAnalysisActions.clearTable,
    });
  };
  const handleLoadAllFavsClick = () => {
    dispatch({
      type: gridTableAnalysisActions.loadAllFavs,
    });
  };
  const handleManageSharedQClick = () => {
    setMode('manage-shares');
  };

  return (
    <Container bottom={4}>
      <FooterSeparator />
      <Container row spaceBetween>
        <Container row>
          {mode === 'manage-shares' && (
            <Container top={24}>
              <ManageFavoriteQuestionSharing onDismiss={() => setMode(null)} />
            </Container>
          )}
          <Container left={16}>
            <SkeletonButton onClick={handleManageSharedQClick}>Manage Shared Questions</SkeletonButton>
          </Container>
          <Container left={16}>
            <SkeletonButton onClick={handleLoadAllFavsClick}>Load All Favorites</SkeletonButton>
          </Container>
        </Container>
        <Container left={16}>
          <SkeletonButton onClick={handleClearImportedTableClick}>Reset Table</SkeletonButton>
        </Container>
      </Container>
    </Container>
  );
};

export const BuildGridModeTable = ({
  onGoBack,
  onRunAnalysis,
  docMode,
  tool,
  selectedAgent,
  onSelectedAgentChange,
}) => {
  React.useEffect(() => {
    TrackingEvent.create('View Table Builder', {
      'Doc Mode': docMode,
    });
  }, []);

  return (
    <Wrapper>
      <Page width={'100%'}>
        <Container>
          <BackArrowButton onClick={onGoBack} />
          <AdvancedSettingsButton tool={tool} />

          <Container top={48}>
            <Header docMode={docMode} />
          </Container>
          <BuildTableQuestionDocumentSelector
            onRunAnalysis={onRunAnalysis}
            docMode={docMode}
            selectedAgent={selectedAgent}
            onSelectedAgentChange={onSelectedAgentChange}
          />
        </Container>
        <TableFooterOptions docMode={docMode} />
      </Page>
    </Wrapper>
  );
};
